import React, { useState, useEffect, useRef } from 'react';

/**
 * Reusable FilterDropdown component for filtering tables.
 * Props:
 * - isOpen: boolean - whether the dropdown is visible
 * - onClose: function - callback to close the dropdown
 * - filters: object - current filter values
 * - setFilters: function - callback to update filters
 * - onApplyFilters: function - callback when Apply is clicked
 * - buttonRef: ref - reference to the button that triggers the dropdown
 * - title: string - title of the filter panel (default: "Filter")
 * 
 * Filter field visibility props (all optional, default false):
 * - showState: boolean - show State dropdown
 * - showStatus: boolean - show Status dropdown
 * - showDates: boolean - show Start/End Date fields
 * - showFirstParty: boolean - show First Party Name dropdown
 * - showClient: boolean - show Client Name dropdown
 * - showVendor: boolean - show Vendor Name dropdown
 * 
 * Data props (for populating dropdowns):
 * - states: array - list of states
 * - firstPartyNames: array - list of first party names
 * - clients: array - list of client names
 * - vendors: array - list of vendor objects with {id, name}
 * - statusOptions: array - custom status options [{value, label}]
 */
const FilterDropdown = ({
    isOpen,
    onClose,
    filters,
    setFilters,
    onApplyFilters,
    buttonRef,
    title = "Filter",
    // Field visibility
    showState = false,
    showStatus = false,
    showDates = false,
    showFirstParty = false,
    showClient = false,
    showVendor = false,
    // Data
    states = [],
    firstPartyNames = [],
    clients = [],
    vendors = [],
    statusOptions = null
}) => {
    // Defensive: ensure all data props are arrays to prevent .map() crashes
    const safeStates = Array.isArray(states) ? states : [];
    const safeFirstPartyNames = Array.isArray(firstPartyNames) ? firstPartyNames : [];
    const safeClients = Array.isArray(clients) ? clients : [];
    const safeVendors = Array.isArray(vendors) ? vendors : [];
    const dropdownRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState(null);

    // Default status options for Orders
    const defaultStatusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'placed', label: 'New' },
        { value: 'assigned', label: 'Assigned' },
        { value: 'sample_submitted', label: 'Sample Submitted' },
        { value: 'sample_approved', label: 'Sample Approved' },
        { value: 'sample_rejected', label: 'Sample Rejected' },
        { value: 'executing', label: 'Executing' },
        { value: 'completed', label: 'Completed' },
        { value: 'delivered', label: 'Delivered' }
    ];

    const activeStatusOptions = statusOptions || defaultStatusOptions;

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                onClose();
            }
        };

        const positionDropdown = () => {
            if (!isOpen || !buttonRef.current || !dropdownRef.current) return;
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const dd = dropdownRef.current;

            // Use measured size or fallbacks matching tailwind w-96 (384px)
            const desiredWidth = 384;
            const width = Math.min(desiredWidth, window.innerWidth - 16);

            // Try to place below; if not enough space, place above
            const verticalGap = 8;
            const belowTop = buttonRect.bottom + verticalGap;
            const aboveTop = buttonRect.top - verticalGap;

            // Horizontal: adaptive alignment based on button position
            let left;
            if (buttonRect.left < window.innerWidth / 2) {
                // If button is on the left side, align to its left edge
                left = Math.max(8, buttonRect.left);
            } else {
                // If button is on the right side, align to its right edge (original behavior)
                // Ensure it doesn't overflow left edge of screen
                left = Math.min(buttonRect.right - width, window.innerWidth - width - 8);
                if (left < 8) left = 8;
            }

            // Compute max height available for below/above and choose
            const maxHeightBelow = window.innerHeight - belowTop - 8;
            const maxHeightAbove = buttonRect.top - 8;
            const openBelow = maxHeightBelow >= Math.min(360, maxHeightAbove);

            let top;
            let maxHeight;
            if (openBelow) {
                top = belowTop;
                maxHeight = Math.max(200, maxHeightBelow);
                dd.style.transform = 'translateY(0)';
            } else {
                top = aboveTop;
                maxHeight = Math.max(200, maxHeightAbove - verticalGap);
                dd.style.transform = 'translateY(-100%)';
            }

            setDropdownStyle({
                position: 'fixed',
                top: Math.round(top),
                left: Math.round(left),
                width: Math.round(width),
                maxHeight: Math.round(maxHeight),
                overflowY: 'auto'
            });
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
            setTimeout(positionDropdown, 0);
            window.addEventListener('resize', positionDropdown);
            window.addEventListener('scroll', positionDropdown, true);
        }

        return () => {
            window.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', positionDropdown);
            window.removeEventListener('scroll', positionDropdown, true);
        };
    }, [isOpen, onClose, buttonRef]);

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="bg-white rounded-lg shadow-lg border border-gray-200 z-50"
            style={dropdownStyle || { position: 'fixed', top: -9999, left: -9999, width: 384 }}
            role="dialog"
            aria-labelledby="filter-title"
        >
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 id="filter-title" className="text-lg font-semibold text-gray-900">{title}</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                        aria-label="Close filters"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {showState && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                            <select
                                value={filters.state || ''}
                                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                aria-label="Filter by state"
                            >
                                <option value="">All States</option>
                                {safeStates.map((state) => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {showStatus && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={filters.status || ''}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                aria-label="Filter by status"
                            >
                                {activeStatusOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {showDates && (
                        <>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={filters.start_date || ''}
                                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    aria-label="Filter by start date"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={filters.end_date || ''}
                                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    aria-label="Filter by end date"
                                />
                            </div>
                        </>
                    )}

                    {showFirstParty && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">First Party Name</label>
                            <select
                                value={filters.first_party_name || ''}
                                onChange={(e) => setFilters({ ...filters, first_party_name: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                aria-label="Filter by first party name"
                            >
                                <option value="">All First Parties</option>
                                {safeFirstPartyNames.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {showClient && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Client Name</label>
                            <select
                                value={filters.client_name || ''}
                                onChange={(e) => setFilters({ ...filters, client_name: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                aria-label="Filter by client name"
                            >
                                <option value="">All Clients</option>
                                {safeClients.map((client) => (
                                    <option key={client} value={client}>{client}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {showVendor && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Vendor Name</label>
                            <select
                                value={filters.vendor_name || ''}
                                onChange={(e) => setFilters({ ...filters, vendor_name: e.target.value })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                aria-label="Filter by vendor name"
                            >
                                <option value="">All Vendors</option>
                                {safeVendors.map((vendor) => (
                                    <option key={vendor.id || vendor.name} value={vendor.name}>{vendor.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onApplyFilters}
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                    >
                        Apply Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FilterDropdown;
